// @ts-check
// Controle de telefone: país à esquerda (Brasil por padrão) e número com máscara de celular ou fixo.
// Usado no formulário de criação e na edição dentro da célula ou do campo do registro.
import { html } from 'htm/preact';
import {
  PHONE_COUNTRIES,
  findPhoneCountry,
  getBrazilianPhoneKind,
  maskPhoneNumber,
  toPhoneDigits,
} from '../../lib/phone.js';
import { t } from '../../i18n/index.js';

/** @typedef {{ countryCode: string, digits: string }} PhoneDraft */

/**
 * @param {{ draft: PhoneDraft, onChange: (draft: PhoneDraft) => void, autoFocus?: boolean, variant?: 'form' | 'inline',
 *   onKeyDown?: (event: KeyboardEvent) => void, showHint?: boolean }} props
 */
export function PhoneNumberControl({ draft, onChange, autoFocus = false, variant = 'form', onKeyDown, showHint = variant === 'form' }) {
  const country = findPhoneCountry(draft.countryCode);
  const kind = getBrazilianPhoneKind(draft.digits, country.countryCode);
  const isInline = variant === 'inline';
  const focus = (/** @type {HTMLInputElement | null} */ element) => {
    if (autoFocus && element && !element.dataset.focused) {
      element.dataset.focused = 'true';
      requestAnimationFrame(() => element.focus());
    }
  };
  return html`<div class="phone-control" data-variant=${variant}>
    <div class="phone-control-row">
      <select
        class=${isInline ? 'field-input-control phone-control-country' : 'text-input phone-control-country'}
        aria-label=${t('fieldInput.phoneCountry')}
        value=${country.countryCode}
        onChange=${(event) => {
          const countryCode = event.currentTarget.value;
          onChange({ countryCode, digits: toPhoneDigits(draft.digits, countryCode) });
        }}
      >
        ${PHONE_COUNTRIES.map((option) => html`<option key=${option.countryCode} value=${option.countryCode}>
          ${option.flag} ${option.label} (${option.callingCode})
        </option>`)}
      </select>
      <input
        class=${isInline ? 'field-input-control phone-control-number' : 'text-input phone-control-number'}
        type="tel"
        inputmode="tel"
        autocomplete="tel-national"
        ref=${focus}
        placeholder=${country.countryCode === 'BR' ? t('fieldInput.phoneBrazilPlaceholder') : t('fieldInput.phone')}
        value=${maskPhoneNumber(draft.digits, country.countryCode)}
        onInput=${(event) => {
          const input = event.currentTarget;
          const digits = toPhoneDigits(input.value, country.countryCode);
          // reaplica a máscara já no campo, para o texto não divergir do estado quando nada muda
          input.value = maskPhoneNumber(digits, country.countryCode);
          onChange({ countryCode: country.countryCode, digits });
        }}
        onKeyDown=${onKeyDown}
      />
    </div>
    ${showHint && kind
      ? html`<span class="phone-control-hint" data-kind=${kind}>${t(`fieldInput.phoneKind.${kind}`)}</span>`
      : null}
  </div>`;
}
